<template>
    <!-- Can be used in Automations  -->
    <div>
        <div class="form-group text-left">
            <label for="name">Name *</label>
            <input :value="authConnection.name"
                   @input="onAuthConnectionNameInput"
                   placeholder="Name"
                   type="text"
                   class="form-control"
                   id="name">
            <small class="form-text text-danger">
                <span v-if="$v.authConnection.name.$error">Name is required</span>
            </small>
        </div>

        <template v-if="isEditOperation">
            <div class="form-group text-left">
                <label>Connection</label>
                <multiselect
                    :value="appConnection"
                    :options="appConnectionsList"
                    @select="appConnectionSelect"
                    trackBy="uuid"
                    label="name"
                    class="multiselect-md"
                    id="app-connection"
                    placeholder="Select a connection">
                </multiselect>
            </div>

            <div v-if="appConnection && appConnectionParams.length !== 0" class="text-left">
                <template v-if="notAdvancedParameters.length !== 0">
                    <div class="text-muted font-12 mb-1 mt-3">Connection parameters</div>
                    <AppParam
                        v-for="(model, idx) in notAdvancedParameters"
                        :key="idx"
                        :prop="model.name"
                        :model="model"
                        :value="getAuthConnectionParamValue(model.name)"
                        :onChange="onConnectionParamValueChange"
                        :mappable="false"
                        class="text-left"/>
                </template>

                <template v-if="advancedParameters.length !== 0">
                    <label @click="expandAdvancedParams = !expandAdvancedParams"
                           class="text-muted clickable mt-2 pr-3">
                        Advanced
                        <AppExpandChevron :show="expandAdvancedParams" class="pl-2"/>
                    </label>
                    <div v-if="expandAdvancedParams">
                        <AppParam
                            v-for="(model, idx) in advancedParameters"
                            :key="idx"
                            :prop="model.name"
                            :model="model"
                            :value="getAuthConnectionParamValue(model.name)"
                            :onChange="onConnectionParamValueChange"
                            :mappable="false"
                            class="text-left"/>
                    </div>
                </template>
            </div>

            <div v-if="isSuccessfullyConnected && appConnectionRequiredParams.length !== 0" class="text-left">
                <div class="text-muted font-12 mb-1 mt-3">Additional required parameters</div>
                <AppParam
                    v-for="(model, idx) in appConnectionRequiredParams"
                    :key="idx"
                    :prop="model.name"
                    :model="model"
                    :value="getAuthConnectionParamValue(model.name)"
                    :onChange="onConnectionRequiredParamValueChange"
                    :mappable="false"
                    class="text-left"/>
            </div>
        </template>
    </div>
</template>

<script>
import AppExpandChevron from '../../../components/shared/AppExpandChevron'
import AppParam from '../../../components/z-parameters/AppParam'
import {required} from 'vuelidate/lib/validators'
import {ConnectionStatuses} from '../../../types/apps/connections'

export default {
    name: 'AppAuthConnectionForm',
    components: {AppExpandChevron, AppParam},
    props: {
        authConnection: { // AppAuthConnection
            type: Object,
            required: true
        },
        appConnectionsList: { // AppConnection[]
            type: Array,
            required: true
        }
    },
    data() {
        return {
            touchMap: new WeakMap(),
            expandAdvancedParams: false
        }
    },
    validations: {
        authConnection: {
            name: {
                required
            }
        }
    },
    computed: {
        isEditOperation() {
            return !!_.get(this.authConnection, 'uuid')
        },

        authConnectionStatus() {
            return this.authConnection.status
        },

        isSuccessfullyConnected() {
            return this.authConnectionStatus === ConnectionStatuses.CONNECTED
        },

        appConnection() {
            return this.appConnectionsList.find(c => c.uuid === _.get(this.authConnection, 'settings.appConnectionUuid'))
        },

        appConnectionParams() {
            return _.isArray(_.get(this.appConnection, 'parameters')) ? this.appConnection.parameters : []
        },

        // TODO: Can contain RPCs => render after successful connection; add RPCs replacer like in block props editor
        appConnectionRequiredParams() {
            return _.isArray(_.get(this.appConnection, 'required_parameters')) ? this.appConnection.required_parameters : []
        },

        advancedParameters() {
            return this.appConnectionParams.filter(p => p.advanced)
        },

        notAdvancedParameters() {
            return this.appConnectionParams.filter(p => !p.advanced)
        }
    },
    methods: {
        updateAuthConnectionProp(path, value) {
            const c = _.cloneDeep(this.authConnection)
            _.set(c, path, value)
            this.$emit('update', c)
        },

        onAuthConnectionNameInput(e) {
            this.vuelidateDelayTouch(this.$v.authConnection.name, this.touchMap)
            this.updateAuthConnectionProp('name', e.target.value)
        },

        appConnectionSelect(appConnection) {
            this.updateAuthConnectionProp('settings.appConnectionUuid', _.get(appConnection, 'uuid'))
        },

        onConnectionParamValueChange({prop, data}) {
            this.updateAuthConnectionProp(['settings', 'params', prop], data)
        },

        onConnectionRequiredParamValueChange({prop, data}) {
            this.updateAuthConnectionProp(['settings', 'required_params', prop], data)
        },

        getAuthConnectionParamValue(rootProp) {
            return {
                [rootProp]: _.get(this.authConnection, ['settings', 'params', rootProp])
            }
        }
    }
}
</script>
